import React, { useEffect, useState } from 'react';
import CustomModal from 'components/custom-modal/CustomModal';
import COFCardDisplay from './COFCardDisplay';
import Button, { ButtonType } from 'components/custom-button/CustomButton';
import { CircleCloseIcon } from 'assets/icons';
import { formatAmount, formatDecimal, getAmountWithCurrency } from 'utils/amountUtils';
import { CustomerCard } from 'containers/customers/customers.model';
import Divider from 'components/divider/Divider';
import { useChargeCustomerCardMutation, useLazyChargeCustomerCardErrorQuery } from 'containers/app/api';
import { InvoiceByIdV2 } from '../invoices.model';
import { selectCurrentTeam, selectCurrentTeamCustomersPermissions } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { VoidFn } from 'types/baseTypes';
import PlusButton from 'components/plus-button/PlusButton';
import { Loader } from 'components';
import { PaymentMode } from 'containers/settings/settings.model';
import { NotifierTypes } from 'constants/notifierConstants';
import { ChargeCustomerCardErrorResponse, ChargeCustomerCardSuccessResponse } from 'containers/app/app.model';
import { showNotification } from 'utils/Notifier';
import { handleChargeCardError } from 'containers/invoices/card-on-file/utils';
import { getCustomerFullName } from 'containers/customers/Utils';
import { getCardType } from 'containers/receipts/util';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsCardOnFile } from './events';
import { buildPaymentContext } from 'utils/payment-context';

interface ChargeCardOnFileModalProps {
  onClose: VoidFn;
  onChargeCardSuccessCallback: ({ cardData, transactionId }: { cardData: CustomerCard; transactionId: string }) => void;
  cardData: CustomerCard[];
  isLoadingCards?: boolean;
  invoiceData: InvoiceByIdV2;
  onDeleteCard?: (card: CustomerCard) => void;
  onEditCard?: (card: CustomerCard) => void;
  onManageCard: (card: CustomerCard) => void;
  handleAddCard: VoidFn;
  onError: ({
    cardData,
    txnId,
    message,
    subtitle,
    canRetry
  }: {
    cardData: CustomerCard;
    txnId: string;
    message: string;
    subtitle?: string;
    canRetry?: boolean;
  }) => void;
  cardOnFileData: PaymentMode;
  amountPayable: number;
}

export const ChargeCardOnFileModal: React.FC<ChargeCardOnFileModalProps> = ({
  onChargeCardSuccessCallback,
  onClose,
  cardData,
  isLoadingCards = false,
  invoiceData,
  handleAddCard,
  onError,
  cardOnFileData,
  onManageCard,
  amountPayable
}) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const dispatch = useAppDispatch();
  const isPartialPayment = amountPayable !== invoiceData.totalAmount;
  const areCardsExpired = cardData?.every(card => card.isExpired);

  const [selectedCard, setSelectedCard] = useState<CustomerCard | null>(cardData[0]);

  useEffect(() => {
    if (cardData && cardData.length > 0) {
      setSelectedCard(cardData[0]);
    } else {
      setSelectedCard(null);
    }
  }, [cardData]);

  const [chargeCustomerCard, { isLoading: isChargingCard }] = useChargeCustomerCardMutation();
  const [getChargeCardErrorStatus] = useLazyChargeCustomerCardErrorQuery();

  useEffect(() => {
    logAnalyticEvent(CleverTapEventsCardOnFile.webCOFChargeCardModalOpen, {
      'Customer Id': invoiceData?.invoiceCustomer?.id,
      'Invoice Id': invoiceData?.invoiceId,
      Amount: amountPayable,
      'Card Count': cardData?.length || 0
    });
  }, []);

  const handleChargeCard = async () => {
    try {
      const customerContext = await buildPaymentContext();
      const response = await chargeCustomerCard({
        teamId: currentTeam.id,
        payload: {
          qrId: invoiceData.qrId,
          amount: amountPayable.toString(),
          serviceChargeAmount:
            !isPartialPayment && invoiceData.serviceCharges?.length > 0
              ? invoiceData.serviceCharges[0].totalAmount.toString()
              : '0',
          customerCardId: cardData[0].id || '',
          customerInfo: {
            email: cardData[0]?.email || '',
            name: cardData[0]?.cardHolderName || '',
            country: cardData[0]?.address?.country || ''
          },
          ...(Object.keys(customerContext).length ? { customerContext } : {})
        }
      }).unwrap();
      if (response?.success) {
        const successResponse = response.data as ChargeCustomerCardSuccessResponse;
        onChargeCardSuccessCallback({
          cardData: cardData[0],
          transactionId: successResponse.transactionId
        });

        logAnalyticEvent(CleverTapEventsCardOnFile.webCOFChargeCardSuccess, {
          'Customer Id': invoiceData?.invoiceCustomer?.id,
          'Invoice Id': invoiceData?.invoiceId,
          'Transaction Id': successResponse.transactionId,
          Amount: amountPayable,
          'Card Network': cardData[0]?.network,
          'Card Last 4': cardData[0]?.cardLast4
        });
      } else {
        const errorResponse = response.data as ChargeCustomerCardErrorResponse;
        const errorDetails = await handleChargeCardError({
          error: errorResponse,
          cardData: cardData[0],
          getChargeCardErrorStatus,
          teamId: currentTeam.id
        });
        onError(errorDetails);
        logAnalyticEvent(CleverTapEventsCardOnFile.webCOFChargeCardStripeFailed, {
          'Customer Id': invoiceData?.invoiceCustomer?.id,
          'Invoice Id': invoiceData?.invoiceId,
          Amount: amountPayable,
          'Card Network': cardData[0]?.network,
          'Card Last 4': cardData[0]?.cardLast4,
          'Error Message': errorDetails.message
        });
      }
    } catch (error: any) {
      const errorDetails = await handleChargeCardError({
        error,
        cardData: cardData[0],
        getChargeCardErrorStatus,
        teamId: currentTeam.id
      });

      logAnalyticEvent(CleverTapEventsCardOnFile.webCOFChargeCardFailed, {
        'Customer Id': invoiceData?.invoiceCustomer?.id,
        'Invoice Id': invoiceData?.invoiceId,
        Amount: amountPayable,
        'Card Network': cardData[0]?.network,
        'Card Last 4': cardData[0]?.cardLast4,
        'Error Message': errorDetails.message
      });

      onError(errorDetails);
    }
  };

  const onChargeCard = () => {
    if (cardOnFileData && !cardOnFileData.enabled) {
      showNotification(
        dispatch,
        NotifierTypes.ERROR,
        'Charge card on file is not enabled on your account yet. Please contact our support team to activate it.'
      );
      return;
    }
    if (+amountPayable <= +cardOnFileData?.maximumAmount) {
      handleChargeCard();
      logAnalyticEvent(CleverTapEventsCardOnFile.webCOFChargeCardInitiated, {
        'Customer Id': invoiceData?.invoiceCustomer?.id,
        'Invoice Id': invoiceData?.invoiceId,
        Amount: amountPayable,
        'Card Network': cardData[0]?.network,
        'Card Last 4': cardData[0]?.cardLast4
      });
    } else {
      showNotification(
        dispatch,
        NotifierTypes.ERROR,
        `Charge card on file can't be used for this payment. It's available only for amounts up to $${formatDecimal(
          formatAmount(+cardOnFileData.maximumAmount)
        )}. Please use a different method or contact support for help.`
      );
    }
  };

  const getPrimaryButtonText = () => {
    if (selectedCard && selectedCard?.network && selectedCard?.cardLast4) {
      return `Charge ${getCardType(selectedCard.network)} **** ${selectedCard.cardLast4}`;
    }
    return 'Charge card';
  };

  return (
    <CustomModal
      open={true}
      closeModal={onClose}
      className="flex flex-col"
      enableOutsideClick={false}
      width="max-w-[375px] md:w-[375px]">
      <div className="relative px-[30px] py-9">
        {isLoadingCards ? (
          <div className="flex items-center justify-center p-4">
            <Loader />
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-primaryText">Charge card on file</h2>
            <button onClick={onClose} className="absolute top-4 right-4" aria-label="Close">
              <CircleCloseIcon />
            </button>
            <div className="flex flex-col gap-5 pt-10">
              <div className="flex flex-col items-center">
                <div className="text-4xl font-bold text-headingBlack">{getAmountWithCurrency(amountPayable)}</div>
              </div>

              <div className="flex w-full items-center justify-between ">
                <Divider variant="fullWidth" className="w-1/5 border-secondaryBtn" />
                <div className="text-px13 text-accentText">
                  {getCustomerFullName(invoiceData.invoiceCustomer)}'s saved card
                </div>
                <Divider variant="fullWidth" className="w-1/5 border-secondaryBtn" />
              </div>

              <div className="flex flex-col gap-2">
                {cardData &&
                  cardData.map(card => (
                    <div
                      key={card.id}
                      className={`rounded border p-3 ${
                        selectedCard?.id === card.id ? 'border-secondary' : 'border-borderGray'
                      }`}>
                      <COFCardDisplay
                        key={card.id}
                        cardData={card}
                        showCardHolderName={true}
                        isEditable={customerPermissions?.editCustomerCards || false}
                        showManageButton={true}
                        onManageCardClick={onManageCard}
                        className="gap-2"
                      />
                    </div>
                  ))}
                {(!cardData || cardData.length === 0) && (
                  <div className="rounded border border-borderGray py-3 px-5">
                    <PlusButton onClick={handleAddCard} id="add-card-btn" label="Save a card to customer profile" />
                  </div>
                )}
              </div>

              {/* Action Button */}
              <Button
                id="charge-card-btn"
                type={ButtonType.PRIMARY}
                onClick={onChargeCard}
                className="w-full"
                disabled={isChargingCard || !cardData || cardData.length === 0 || areCardsExpired}
                isLoading={isChargingCard}
                childrenContainerStyles="text-17px">
                {getPrimaryButtonText()}
              </Button>

              <Button
                id="cancel-charge-card-btn"
                type={ButtonType.SECONDARY}
                onClick={onClose}
                className="w-full"
                disabled={isChargingCard}
                childrenContainerStyles="text-17px">
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default ChargeCardOnFileModal;
